import { useState, useCallback, useEffect, useRef } from 'react'
import { parseIntervalToDays } from '~/common/utils/date-utils'

const WEEK_CONTAINER_WIDTH = 32
const PIXELS_PER_DAY = WEEK_CONTAINER_WIDTH / 7  // ~4.57px

const MIN_TASK_DURATIONS = {
  seeding: 1,
  transplanting: 1,
  weeding: 3,
  fertilizing: 1,
  harvesting: 7,
  default: 1
}

/**
 * Custom hook for dragging task boundaries on succession child rows.
 *
 * Mirrors useTaskDrag but scoped to a specific succession childId.
 * handleDragStart receives (childId, tasks, intersectionIndex, clientX)
 * where intersectionIndex is { type: 'start'|'end', taskIndex: number }.
 * draggingState / hoveredState are { childId, intersectionIndex } objects.
 *
 * @param {Object} options
 * @param {Function} options.onSuccessionTaskAdjust - (childId, tasks) => void
 * @returns {Object} { isDragging, draggingState, hoveredState, setHoveredState, handleDragStart }
 */
export const useSuccessionTaskDrag = ({ onSuccessionTaskAdjust }) => {
  const [dragState, setDragState] = useState({
    isDragging: false,
    childId: null,
    intersectionIndex: null,
    startX: 0,
    startTasks: null,
  })

  const [hoveredState, setHoveredState] = useState(null) // { childId, intersectionIndex } | null

  const dragStateRef = useRef(dragState)
  const onAdjustRef = useRef(onSuccessionTaskAdjust)

  useEffect(() => {
    dragStateRef.current = dragState
    onAdjustRef.current = onSuccessionTaskAdjust
  }, [dragState, onSuccessionTaskAdjust])

  const handleDragStart = useCallback((childId, tasks, intersectionIndex, clientX) => {
    if (!tasks || tasks.length === 0) return
    setDragState({
      isDragging: true,
      childId,
      intersectionIndex,
      startX: clientX,
      startTasks: tasks.map(t => ({
        ...t,
        _startDuration: parseIntervalToDays(t.duration),
      })),
    })
  }, [])

  const handleDragMove = useCallback((e) => {
    const state = dragStateRef.current
    if (!state.isDragging || !state.startTasks) return

    const deltaX = e.clientX - state.startX
    const deltaDays = Math.round(deltaX / PIXELS_PER_DAY)
    if (deltaDays === 0) return

    const { intersectionIndex, startTasks } = state
    if (!intersectionIndex || typeof intersectionIndex !== 'object') return

    const { type, taskIndex } = intersectionIndex
    let updatedTasks

    if (type === 'start') {
      updatedTasks = handleTaskStartDrag(startTasks, taskIndex, deltaDays)
    } else if (type === 'end') {
      updatedTasks = handleTaskEndDrag(startTasks, taskIndex, deltaDays)
    }

    if (!updatedTasks) return
    const cleanedTasks = updatedTasks.map(({ _startDuration, ...t }) => t)
    onAdjustRef.current?.(state.childId, cleanedTasks)
  }, [])

  const handleDragEnd = useCallback(() => {
    setDragState({ isDragging: false, childId: null, intersectionIndex: null, startX: 0, startTasks: null })
  }, [])

  useEffect(() => {
    if (dragState.isDragging) {
      document.addEventListener('mousemove', handleDragMove)
      document.addEventListener('mouseup', handleDragEnd)
      return () => {
        document.removeEventListener('mousemove', handleDragMove)
        document.removeEventListener('mouseup', handleDragEnd)
      }
    }
  }, [dragState.isDragging, handleDragMove, handleDragEnd])

  return {
    isDragging: dragState.isDragging,
    draggingState: dragState.isDragging
      ? { childId: dragState.childId, intersectionIndex: dragState.intersectionIndex }
      : null,
    hoveredState,
    setHoveredState,
    handleDragStart,
  }
}

function handleTaskStartDrag(startTasks, taskIndex, deltaDays) {
  const task = startTasks[taskIndex]
  if (!task) return null

  const minDuration = MIN_TASK_DURATIONS[task.task_code] || MIN_TASK_DURATIONS.default
  const newDuration = task._startDuration - deltaDays
  if (newDuration < minDuration) return null

  const originalStart = new Date(task.task_window_start)
  const originalEnd = new Date(originalStart)
  originalEnd.setDate(originalEnd.getDate() + task._startDuration)

  const newStartDate = new Date(originalEnd)
  newStartDate.setDate(newStartDate.getDate() - newDuration)

  return startTasks.map((t, idx) =>
    idx === taskIndex
      ? { ...t, task_window_start: newStartDate.toISOString(), duration: `${newDuration} days` }
      : t
  )
}

function handleTaskEndDrag(startTasks, taskIndex, deltaDays) {
  const task = startTasks[taskIndex]
  if (!task) return null

  const minDuration = MIN_TASK_DURATIONS[task.task_code] || MIN_TASK_DURATIONS.default
  const newDuration = task._startDuration + deltaDays
  if (newDuration < minDuration) return null

  return startTasks.map((t, idx) =>
    idx === taskIndex
      ? { ...t, duration: `${newDuration} days` }
      : t
  )
}
